import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import {
  CreateTaskPayload,
  ResolvedTaskLink,
  Task,
  TaskPriority,
  TaskReminderPayload,
  UpdateTaskPayload,
} from './tasks.types';
import {
  fromPickerValues,
  getLinkKey,
  isSameSet,
  TASK_TITLE_MAX_LENGTH,
  toPickerValues,
  toTaskLink,
} from './tasks.utils';

/** The fields of a task that the form of a new task and the task card share */
export type TaskFormGroup = FormGroup<{
  title: FormControl<string>;
  description: FormControl<string | null>;
  assignee_id: FormControl<string | null>;
  observer_ids: FormControl<string[]>;
  /** `yyyy-mm-dd`, as the date picker holds it */
  deadline_date: FormControl<string | null>;
  /** `HH:mm`: the time belongs to a date, so it is disabled until there is one */
  deadline_time: FormControl<string | null>;
  priority: FormControl<TaskPriority>;
  requires_review: FormControl<boolean>;
  links: FormControl<ResolvedTaskLink[]>;
}>;

/** The backend trims the title and refuses an empty one, so a blank title is as good as none */
const notBlank: ValidatorFn = (control) => (String(control.value ?? '').trim() ? null : { required: true });

export function createTaskForm(): TaskFormGroup {
  return new FormGroup({
    title: new FormControl('', {
      nonNullable: true,
      validators: [notBlank, Validators.maxLength(TASK_TITLE_MAX_LENGTH)],
    }),
    description: new FormControl<string | null>(''),
    assignee_id: new FormControl<string | null>(null, [Validators.required]),
    observer_ids: new FormControl<string[]>([], { nonNullable: true }),
    deadline_date: new FormControl<string | null>(''),
    deadline_time: new FormControl<string | null>({ value: '', disabled: true }),
    priority: new FormControl<TaskPriority>('NORMAL', { nonNullable: true }),
    requires_review: new FormControl(false, { nonNullable: true }),
    links: new FormControl<ResolvedTaskLink[]>([], { nonNullable: true }),
  });
}

/** A picked date starts at midnight and a cleared one takes its time with it */
export function syncDeadlineTime(form: TaskFormGroup): void {
  const { deadline_date, deadline_time } = form.controls;
  if (deadline_date.value) {
    deadline_time.enable({ emitEvent: false });
    if (!deadline_time.value) deadline_time.setValue('00:00', { emitEvent: false });
    return;
  }

  deadline_time.reset('', { emitEvent: false });
  deadline_time.disable({ emitEvent: false });
}

/** Fills the form with the task and makes it the clean state: the changes are counted from it */
export function fillTaskForm(form: TaskFormGroup, task: Task, links: ResolvedTaskLink[]): void {
  const deadline = toPickerValues(task.deadline);
  form.reset({
    title: task.title,
    description: task.description ?? '',
    assignee_id: task.assignee_id,
    observer_ids: [...task.observer_ids],
    deadline_date: deadline.date,
    deadline_time: deadline.time,
    priority: task.priority,
    requires_review: task.requires_review,
    links,
  });
  if (deadline.date) form.controls.deadline_time.enable({ emitEvent: false });
  else form.controls.deadline_time.disable({ emitEvent: false });
}

/**
 * Fills the form with a newer version of the task and keeps what the user changed since the older one: the fields that
 * differ from `previous` stay as they were typed, all the others take the new values. So what someone else changed in
 * the meantime is not sent back with the next save.
 */
export function refillTaskForm(form: TaskFormGroup, previous: Task, next: Task, links: ResolvedTaskLink[]): void {
  const edited = buildUpdatePayload(form, previous);
  const typed = form.getRawValue();
  fillTaskForm(form, next, links);

  const { controls } = form;
  const keep = <T>(control: FormControl<T>, value: T): void => {
    control.setValue(value);
    control.markAsDirty();
  };
  if ('title' in edited) keep(controls.title, typed.title);
  if ('description' in edited) keep(controls.description, typed.description);
  if ('assignee_id' in edited) keep(controls.assignee_id, typed.assignee_id);
  if ('deadline' in edited) {
    keep(controls.deadline_date, typed.deadline_date);
    syncDeadlineTime(form);
    if (typed.deadline_date) controls.deadline_time.setValue(typed.deadline_time);
  }
  if ('priority' in edited) keep(controls.priority, typed.priority);
  if ('requires_review' in edited) keep(controls.requires_review, typed.requires_review);
  if ('observer_ids' in edited) keep(controls.observer_ids, typed.observer_ids);
  if ('links' in edited) keep(controls.links, typed.links);
}

/** The deadline of the form as the API takes it: ISO to the minute, none without a date */
export function getFormDeadline(form: TaskFormGroup): string | null {
  const { deadline_date, deadline_time } = form.getRawValue();
  return fromPickerValues(deadline_date, deadline_time);
}

/** Only what is filled in: the backend takes an omitted field as not set */
export function buildCreatePayload(form: TaskFormGroup, reminders: TaskReminderPayload[]): CreateTaskPayload {
  const value = form.getRawValue();
  const description = value.description?.trim();
  const deadline = getFormDeadline(form);

  return {
    title: value.title.trim(),
    ...(description ? { description } : {}),
    ...(value.assignee_id ? { assignee_id: value.assignee_id } : {}),
    ...(value.observer_ids.length ? { observer_ids: value.observer_ids } : {}),
    ...(deadline ? { deadline } : {}),
    priority: value.priority,
    requires_review: value.requires_review,
    ...(value.links.length ? { links: value.links.map(toTaskLink) } : {}),
    ...(reminders.length ? { reminders } : {}),
  };
}

/** The same moment, or no moment on both sides */
const isSameMoment = (a: string | null, b: string | null): boolean =>
  a === b || (!!a && !!b && new Date(a).getTime() === new Date(b).getTime());

/** Only what differs from the task: `null` clears the description and removes the deadline, the lists are sent whole */
export function buildUpdatePayload(form: TaskFormGroup, task: Task): UpdateTaskPayload {
  const value = form.getRawValue();
  const payload: UpdateTaskPayload = {};

  const title = value.title.trim();
  if (title !== task.title) payload.title = title;

  const description = value.description?.trim() || null;
  if (description !== (task.description || null)) payload.description = description;

  if (value.assignee_id && value.assignee_id !== task.assignee_id) payload.assignee_id = value.assignee_id;

  const deadline = getFormDeadline(form);
  if (!isSameMoment(deadline, task.deadline)) payload.deadline = deadline;

  if (value.priority !== task.priority) payload.priority = value.priority;
  if (value.requires_review !== task.requires_review) payload.requires_review = value.requires_review;
  if (!isSameSet(value.observer_ids, task.observer_ids)) payload.observer_ids = value.observer_ids;

  const links = value.links.map(toTaskLink);
  if (!isSameSet(links.map(getLinkKey), task.links.map(getLinkKey))) payload.links = links;

  return payload;
}
